import React from 'react';
import { CustomSelect } from '../../../../shared/components/CustomSelect';
import { DynamicsChartId, DynamicsPrefs } from './dynamicsPrefs';

const CHART_META: Record<DynamicsChartId, { label: string; hint: string }> = {
  A: {
    label: 'A · Plan vs Done',
    hint: 'Plan, Done, Dev debt, QA debt, Verification failed across sprints/releases — the classic team rhythm chart with trend lines.',
  },
  B: {
    label: 'B · Bug flow',
    hint: 'Stacked area of open-bug statuses over time. Sees whether the bug backlog is growing or shrinking.',
  },
  C: {
    label: 'C · Arrival vs Closure',
    hint: 'Bugs created vs bugs resolved per period, with the net backlog change shaded.',
  },
  D: {
    label: 'D · MTTR',
    hint: 'Mean time to resolve bugs over time. Priority-level breakdown available via the legend.',
  },
  E: {
    label: 'E · Escaped defects',
    hint: 'Bugs filed AFTER a release that reference that fix_version (or affected_versions). Strongest prod-quality signal.',
  },
  G: {
    label: 'G · QA throughput',
    hint: 'Issues verified by each QA engineer per period. Use as a health metric, not to score individuals.',
  },
  H: {
    label: 'H · Reopen dynamics',
    hint: 'Number of reopen transitions (Done/Verified → In Progress) per period.',
  },
  I: {
    label: 'I · Sprint predictability',
    hint: 'Done ÷ Plan % per sprint, with a reference line at 80%. Helps negotiate realistic commitments.',
  },
  J: {
    label: 'J · Bug origin',
    hint: 'Who filed the bug (QA / Developer / Support). A growing Support share = escapes reaching production.',
  },
  K: {
    label: 'K · Escape taxonomy',
    hint: 'Stacked share of escape root causes over time (regression / missed req / env / data / flaky). Requires the Jira root-cause field to be configured in Quality settings.',
  },
};

interface DynamicsControlsProps {
  prefs: DynamicsPrefs;
  onChange: (next: DynamicsPrefs) => void;
}

export const DynamicsControls: React.FC<DynamicsControlsProps> = ({ prefs, onChange }) => {
  const toggle = (id: DynamicsChartId) => {
    const set = new Set(prefs.enabled);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    onChange({ ...prefs, enabled: Array.from(set) });
  };

  return (
    <div className="dynamics-controls">
      <div className="dynamics-controls__group">
        <label htmlFor="dynamics-groupby">Group by</label>
        <CustomSelect
          id="dynamics-groupby"
          value={prefs.groupBy}
          onChange={(val) => onChange({ ...prefs, groupBy: val as DynamicsPrefs['groupBy'] })}
          options={[
            { value: 'sprint', label: 'Sprint' },
            { value: 'release', label: 'Release' },
            { value: 'week', label: 'Week' },
            { value: 'month', label: 'Month' },
          ]}
        />
      </div>
      <div className="dynamics-controls__group">
        <label htmlFor="dynamics-lastn">Last</label>
        <CustomSelect
          id="dynamics-lastn"
          value={String(prefs.lastN)}
          onChange={(val) => onChange({ ...prefs, lastN: parseInt(val, 10) || 8 })}
          options={[4, 6, 8, 10, 12, 16, 24].map((n) => ({ value: String(n), label: String(n) }))}
        />
      </div>
      <div className="dynamics-controls__chips">
        {(Object.keys(CHART_META) as DynamicsChartId[]).map((id) => {
          const active = prefs.enabled.includes(id);
          return (
            <button
              key={id}
              type="button"
              className={`dynamics-chip${active ? ' dynamics-chip--on' : ''}`}
              onClick={() => toggle(id)}
              title={CHART_META[id].hint}
            >
              {CHART_META[id].label}
            </button>
          );
        })}
      </div>
      <label
        className="dynamics-controls__hide-empty"
        title="Hide Dynamics cards that have no data for the current Period and Group by. Helpful when you have many cards enabled but only a subset has matching issues."
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}
      >
        <input
          type="checkbox"
          checked={prefs.hideEmpty}
          onChange={(e) => onChange({ ...prefs, hideEmpty: e.target.checked })}
        />
        Show only cards with data
      </label>
    </div>
  );
};
